import React, { createContext, useEffect, useState, useCallback } from "react";
import AuthenticationService from "../services/AuthenticationService";
import WalletService from "../services/WalletService";
import { isSupportedCurrency } from "../constants/currencies";
import { trackAnalyticsEvent } from "../analytics/analytics";

const getSavedCurrency = () => {
  const savedCurrency = localStorage.getItem("user_currency") || "EUR";
  if (isSupportedCurrency(savedCurrency)) return savedCurrency.toUpperCase();

  localStorage.setItem("user_currency", "EUR");
  return "EUR";
};

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userId, setUserId] = useState(-1);
  const [token, setToken] = useState(() => localStorage.getItem("token") || null);
  const [firebaseToken, setFirebaseToken] = useState(() => localStorage.getItem("firebaseToken") || null);
  const [role, setRole] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [walletCurrency, setWalletCurrency] = useState(() => {
    if (typeof window !== "undefined") {
      return getSavedCurrency();
    }
    return "USD";
  });

  const [twoFactorMethod, setTwoFactorMethod] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshWallets = useCallback(async () => {
    if (!token) return;
    try {
      const walletData = await WalletService.getUserWallet();
      setWallet(walletData);
      if (walletData?.currency) {
        setWalletCurrency(walletData.currency);
        localStorage.setItem("user_currency", walletData.currency);
      }
      return walletData;
    } catch (err) {
      console.error("Failed to refresh wallet:", err);
    }
  }, [token]);

  const changeCurrency = async (newCurrency) => {
    if (!isSupportedCurrency(newCurrency)) return;
    newCurrency = newCurrency.toUpperCase();
    setWalletCurrency(newCurrency);
    localStorage.setItem("user_currency", newCurrency);

    if (token && wallet?.id) {
      try {
        await WalletService.changeWalletCurrency(wallet.id, newCurrency);
        await refreshWallets();
      } catch (err) {
        console.error("Failed to update wallet currency on server:", err);
      }
    }
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, [token]);

  useEffect(() => {
    if (firebaseToken) {
      localStorage.setItem("firebaseToken", firebaseToken);
    } else {
      localStorage.removeItem("firebaseToken");
    }
  }, [firebaseToken]);

  useEffect(() => {
    const verifyToken = async () => {
      const savedCurrency = getSavedCurrency();

      if (!token) {
        setUserId(-1);
        trackAnalyticsEvent.setUserId(null);
        setRole(null);
        setTwoFactorMethod(null);
        setWallet(null);
        setWalletCurrency(savedCurrency);
        setLoading(false);
        return;
      }

      try {
        const response = await AuthenticationService.checkToken();
        const activeUserId = response.user.email || response.user.id || -1;
        setUserId(activeUserId);
        trackAnalyticsEvent.setUserId(activeUserId);
        setRole(response.user.role || null);
        setTwoFactorMethod(response.user.two_factor_method || null);

        const walletData = await WalletService.getUserWallet();
        setWallet(walletData);

        const activeCur = walletData?.currency || savedCurrency || "EUR";
        setWalletCurrency(activeCur);
        localStorage.setItem("user_currency", activeCur);
      } catch (error) {
        console.error("Token check failed:", error);
        setToken(null);
        setUserId(-1);
        trackAnalyticsEvent.setUserId(null);
        setRole(null);
        setTwoFactorMethod(null);
        setWallet(null);
        setWalletCurrency(savedCurrency);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        firebaseToken,
        setFirebaseToken,
        userId,
        setUserId,
        role,
        setRole,
        wallet,
        setWallet,
        walletCurrency,
        setWalletCurrency,
        changeCurrency,
        refreshWallets,
        loading,
        twoFactorMethod,
        setTwoFactorMethod,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
